import { PaginationSize } from "@/common/enum/pagination-size";
import { QuestionMarkOutlineIcon } from "@/components/icon/QuestionMarkOutline";
import { Body14Regular } from "@/components/typography/Body14Regular";
import CanPayLaterModal from "./CanPayLaterModal";
import { gradeService } from "@/services/grade-service";
import { GradeDto } from "@/types/dto/grade-dto";
import { UserDto } from "@/types/dto/user-dto";
import { Button, Select, Table, ToggleSwitch } from "flowbite-react";
import { useEffect, useState } from "react";
import { BusinessType } from "../../../common/enum/business-type";
import { formatCommasNumber } from "../../../common/util/format-number-with-commas";
import { ImageOutlineIcon } from "../../../components/icon/ImageOutlineIcon";
import { PartnerBusinessCertificateModal } from "../../../components/modal/PartnerBusinessCertificateModal";
import { H4 } from "../../../components/typography/H4";
import { CustomCell } from "../../../components/table/CustomCell";
import { userService } from "@/services/user-service";
import { Body14TextInput } from "@/components/input/Body14TextInput";
import { EditOutlineIcon } from "@/components/icon/EditOutlineIcon";
import { ButtonColor } from "@/common/enum/button-color";

interface Props {
  user: UserDto;
  setUser: (user: UserDto) => void;
  selectedGrade: GradeDto;
  handleSelectedGradeChange: (grade: GradeDto) => void;
  partnerInfo: {
    partnerName: string;
    businessName: string;
    email: string;
    phone: string;
    createdAt: string;
  };
  setPartnerInfo: (partnerInfo: {
    partnerName: string;
    businessName: string;
    email: string;
    phone: string;
    createdAt: string;
  }) => void;
  handleAddressChangeModal: () => void;
}

export const PartnerDetailTable: React.FC<Props> = ({
  user,
  setUser,
  selectedGrade,
  handleSelectedGradeChange,
  partnerInfo,
  setPartnerInfo,
  handleAddressChangeModal,
}) => {
  const [openBizCertModal, setOpenBizCertModal] = useState(false);
  const [openCanPayLaterModal, setOpenCanPayLaterModal] = useState(false);
  const [grades, setGrades] = useState<GradeDto[]>([]);
  const headCellStyle = "font-medium text-gray-900 bg-gray-100";

  const handleOpenBizCertModal = () => {
    setOpenBizCertModal(true);
  };

  const handleCloseBizCertModal = () => {
    setOpenBizCertModal(false);
  };

  const getGrade = async () => {
    const response = await gradeService.getPage({
      page: 1,
      size: PaginationSize.SIZE_1000,
    });
    if (response.ok) {
      setGrades(response.data.contents);
    }
  };

  useEffect(() => {
    getGrade();
  }, []);

  return (
    <div className="overflow-x-auto flex flex-col gap-[16px]">
      {openBizCertModal && (
        <PartnerBusinessCertificateModal
          imageUrl={user.businessCertificateUrl}
          closeModal={handleCloseBizCertModal}
        />
      )}
      <div className="overflow-hidden border rounded-lg">
        <Table>
          <Table.Body>
            <Table.Row className="border-b border-gray-200">
              {/* 파트너명 셀 */}
              <Table.Cell className={`${headCellStyle} w-[112px] p-0 h-[52px]`}>
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    파트너명
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[226px] bg-primary-white">
                <CustomCell>
                  <Body14TextInput
                    className="text-semantic-text-body"
                    maxLength={100}
                    disabled={false}
                    onChange={(value: string) => {
                      setPartnerInfo({
                        ...partnerInfo,
                        partnerName: value,
                      });
                    }}
                  >
                    {partnerInfo.partnerName || ""}
                  </Body14TextInput>
                </CustomCell>
              </Table.Cell>

              <Table.Cell
                className={`${headCellStyle} w-[112px] p-0 h-[52px] border-l`}
              >
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    상호명
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[226px] bg-primary-white">
                <CustomCell>
                  <Body14TextInput
                    className="text-semantic-text-body"
                    maxLength={100}
                    disabled={false}
                    onChange={(value: string) => {
                      setPartnerInfo({
                        ...partnerInfo,
                        businessName: value,
                      });
                    }}
                  >
                    {partnerInfo.businessName || ""}
                  </Body14TextInput>
                </CustomCell>
              </Table.Cell>
              <Table.Cell
                className={`${headCellStyle} w-[112px] p-0 h-[52px] border-l`}
              >
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    파트너 구분
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[226px] bg-primary-white">
                <CustomCell>
                  <Body14Regular className="text-semantic-text-body">
                    {user.businessType === BusinessType.COMPANY
                      ? "법인 사업자"
                      : "개인 사업자"}
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
            </Table.Row>
            <Table.Row className="border-b border-gray-200">
              <Table.Cell className={`${headCellStyle} w-[112px] p-0 h-[52px]`}>
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    이메일(아이디)
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[226px] bg-primary-white">
                <CustomCell>
                  <Body14TextInput
                    className="text-semantic-text-body"
                    maxLength={100}
                    disabled={false}
                    onChange={(value: string) => {
                      setPartnerInfo({
                        ...partnerInfo,
                        email: value,
                      });
                    }}
                  >
                    {partnerInfo.email}
                  </Body14TextInput>
                </CustomCell>
              </Table.Cell>
              <Table.Cell
                className={`${headCellStyle} w-[112px] p-0 h-[52px] border-l`}
              >
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    연락처
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[226px] bg-primary-white">
                <CustomCell>
                  <Body14TextInput
                    className="text-semantic-text-body"
                    maxLength={100}
                    disabled={false}
                    onChange={(value: string) => {
                      setPartnerInfo({
                        ...partnerInfo,
                        phone: value,
                      });
                    }}
                  >
                    {partnerInfo.phone || ""}
                  </Body14TextInput>
                </CustomCell>
              </Table.Cell>
              <Table.Cell
                className={`${headCellStyle} w-[112px] p-0 h-[52px] border-l`}
              >
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    주소
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[226px] bg-primary-white">
                <CustomCell className="flex items-center">
                  <Body14Regular className="text-semantic-text-body">
                    {`[${user.postalCode}] ${user.road || user.jibun || ""} ${
                      user.detailAddress
                    }`}
                  </Body14Regular>
                  <div className="ml-auto">
                    <Button
                      onClick={handleAddressChangeModal}
                      color={ButtonColor.WHITE}
                    >
                      <EditOutlineIcon></EditOutlineIcon>
                    </Button>
                  </div>
                </CustomCell>
              </Table.Cell>
            </Table.Row>
            <Table.Row className="border-gray-200">
              <Table.Cell className={`${headCellStyle} w-[112px] p-0 h-[52px]`}>
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    사업자 번호
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[226px] bg-primary-white">
                <CustomCell className="gap-[8px] items-center">
                  <Body14Regular className="text-semantic-text-body">
                    {user.businessNumber}
                  </Body14Regular>
                  <div
                    className="w-[16px] h-[16px] flex items-center justify-center"
                    onClick={handleOpenBizCertModal}
                  >
                    <ImageOutlineIcon color="text-grayscale-contrast-40" />
                  </div>
                </CustomCell>
              </Table.Cell>

              <Table.Cell
                className={`${headCellStyle} w-[112px] p-0 h-[52px] border-l`}
              >
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    파트너 등급
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>

              <Table.Cell className="w-[226px] bg-primary-white">
                <Select
                  className="w-full m-0 py-0 px-[6px]"
                  color="white"
                  value={selectedGrade.id}
                  onChange={(e) => {
                    const a =
                      grades.find(
                        (grade) => grade.id === Number(e.target.value)
                      ) || selectedGrade;

                    console.log(a);
                    handleSelectedGradeChange(
                      grades.find(
                        (grade) => grade.id === Number(e.target.value)
                      ) || selectedGrade
                    );
                  }}
                >
                  {grades.map((grade) => (
                    <option key={grade.id} value={grade.id}>
                      {grade.name}
                    </option>
                  ))}
                </Select>
              </Table.Cell>

              <Table.Cell
                className={`${headCellStyle} w-[112px] p-0 h-[52px] border-l`}
              >
                <CustomCell className="flex items-center gap-2">
                  <Body14Regular className="text-semantic-text-secondary">
                    후불결제
                  </Body14Regular>
                  <div
                    className="hover:cursor-pointer"
                    onClick={() => setOpenCanPayLaterModal(true)}
                  >
                    <QuestionMarkOutlineIcon />
                  </div>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[226px] bg-primary-white">
                <CustomCell>
                  <div className="flex items-center justify-between w-full">
                    <Body14Regular
                      className={
                        user.canPayLater
                          ? "text-semantic-text-error-primary"
                          : "text-semantic-text-body"
                      }
                    >
                      {user.canPayLater ? "활성화" : "비활성화"}
                    </Body14Regular>
                    <ToggleSwitch
                      checked={user.canPayLater}
                      onClick={() => setOpenCanPayLaterModal(true)}
                      onChange={() => {}}
                    />
                  </div>
                </CustomCell>
              </Table.Cell>

              <CanPayLaterModal
                show={openCanPayLaterModal}
                onClose={() => setOpenCanPayLaterModal(false)}
                isActive={user.canPayLater}
                onActivate={async () => {
                  try {
                    const response = await userService.updateCanPayLater(
                      user.id,
                      !user.canPayLater
                    );
                    if (response.ok) {
                      // Update the user data with the response
                      setUser(response.data);
                    }
                    setOpenCanPayLaterModal(false);
                  } catch (error) {
                    console.error(
                      "Error updating can pay later status:",
                      error
                    );
                  }
                }}
              />
            </Table.Row>

            {/* 가입일시 행 (새로 추가) */}
            <Table.Row className="border-t border-gray-200">
              <Table.Cell className={`${headCellStyle} w-[112px] p-0 h-[52px]`}>
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    가입일시
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[226px] bg-primary-white">
                <CustomCell>
                  <Body14Regular className="text-semantic-text-body">
                    {partnerInfo.createdAt.substring(0, 10) || ""}
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell
                className={`${headCellStyle} w-[112px] p-0 h-[52px] border-l border-gray-200`}
              >
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    가입코드
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[226px] bg-primary-white">
                <CustomCell>
                  <Body14Regular className="text-semantic-text-body">
                    {user.signupCode || ""}
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell
                className={`${headCellStyle} w-[112px] p-0 h-[52px] border-l border-gray-200`}
              >
                <CustomCell>
                  <Body14Regular className="text-semantic-text-secondary">
                    {""}
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
              <Table.Cell className="w-[226px] bg-primary-white">
                <CustomCell>
                  <Body14Regular className="text-semantic-text-body">
                    {""}
                  </Body14Regular>
                </CustomCell>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
      <div className="overflow-hidden border rounded-lg">
        <Table>
          <Table.Body>
            <Table.Row className="bg-grayscale-base">
              <Table.Cell className="p-0  h-[90px] w-[538.67px]">
                <div className="flex flex-col gap-[8px] p-[16px] ">
                  <div>
                    <Body14Regular className="text-semantic-text-tertiary">
                      견적 수
                    </Body14Regular>
                  </div>
                  <div>
                    <H4 className="text-secondary-2-orange">
                      {user.quoteCount}
                    </H4>
                  </div>
                </div>
              </Table.Cell>
              <Table.Cell className="p-0 border-l  h-[90px] w-[538.67px]">
                <div className="flex flex-col gap-[8px] p-[16px] ">
                  <div>
                    <Body14Regular className="text-semantic-text-tertiary">
                      발주 수
                    </Body14Regular>
                  </div>
                  <div>
                    <H4 className="text-secondary-2-orange">
                      {user.orderCount}
                    </H4>
                  </div>
                </div>
              </Table.Cell>
              <Table.Cell className="p-0 border-l  h-[90px] w-[538.67px]">
                <div className="flex flex-col gap-[8px] p-[16px] ">
                  <div>
                    <Body14Regular className="text-semantic-text-tertiary">
                      총 실결제 금액
                    </Body14Regular>
                  </div>
                  <div>
                    <H4 className="text-secondary-2-orange">
                      {formatCommasNumber(user.totalSales)}
                    </H4>
                  </div>
                </div>
              </Table.Cell>
            </Table.Row>
          </Table.Body>
        </Table>
      </div>
    </div>
  );
};
